import Phaser from 'phaser';
import { GameScene } from '../GameScene';

/**
 * Luz ambiental por entidad (LightingSystem) + helpers legacy de la textura de sombra elíptica
 * (hoy deshabilitada, SHADOWS_ENABLED=false), extraídos de GameScene (god-object). Cosmético.
 * Accede a la escena vía `this.scene.*`.
 */
export class EntityLightSystem {
  constructor(private scene: GameScene) {}

  createShadowTexture(): void {
    // Check if texture already exists
    if (this.scene.textures.exists(GameScene.SHADOW_TEXTURE_KEY)) {
      return;
    }

    const width = GameScene.SHADOW_TEXTURE_WIDTH;
    const height = GameScene.SHADOW_TEXTURE_HEIGHT;
    
    // Create a canvas for pixel-level control of the gradient
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('[GameScene] Failed to create canvas context for shadow texture');
      return;
    }
    
    // Create a radial gradient for soft shadow
    const centerX = width / 2;
    const centerY = height / 2;
    const radiusX = width / 2;
    const radiusY = height / 2;
    
    // Save context, scale to create ellipse from circle
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(1, radiusY / radiusX);
    
    // Create radial gradient - PURE BLACK with smooth falloff
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radiusX);
    
    // Dark black center with smooth fade to transparent
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.8)');      // Center: very dark
    gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.6)');
    gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.4)');
    gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.2)');
    gradient.addColorStop(0.9, 'rgba(0, 0, 0, 0.05)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');         // Edge: fully transparent
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, radiusX, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
    
    // Add texture to Phaser
    this.scene.textures.addCanvas(GameScene.SHADOW_TEXTURE_KEY, canvas);
    
    console.log('[GameScene] Soft shadow texture created');
  }

  /**
   * Helper method to create a simple elliptical shadow sprite.
   * Uses the pre-generated soft shadow texture.
   */
  createShadowSprite(
    x: number,
    y: number,
    depth: number,
    scaleMultiplier: number = 1.0
  ): Phaser.GameObjects.Sprite | null {
    // Shadows are currently disabled
    if (!GameScene.SHADOWS_ENABLED) {
      return null;
    }
    
    // Calculate initial shadow position based on current sun angle
    const maxDistance = 16;
    const shadowDistance = this.scene.shadowLength * maxDistance;
    const shadowOffsetX = Math.cos(this.scene.shadowAngle) * shadowDistance;
    const shadowOffsetY = Math.sin(this.scene.shadowAngle) * shadowDistance * 0.5 + 2;
    
    const shadow = this.scene.add.sprite(
      x + shadowOffsetX,
      y + shadowOffsetY,
      GameScene.SHADOW_TEXTURE_KEY
    );
    shadow.setOrigin(0.5, 0.5);
    shadow.setScrollFactor(1);
    shadow.setAlpha(this.scene.shadowAlpha);
    
    const baseScale = 0.8 * scaleMultiplier;
    const stretchFactor = 1 + this.scene.shadowLength * 0.6;
    shadow.setScale(baseScale * stretchFactor, baseScale * 0.6);
    shadow.setDepth(depth);
    return shadow;
  }
  
  /**
   * Registers an entity's light source with the lighting system.
   * The light will be visible when there's ambient darkness.
   */
  registerEntityLight(entityId: string, x: number, y: number): void {
    if (this.scene.lightingSystem) {
      this.scene.lightingSystem.addLightSource(`entity_${entityId}`, {
        x: x,
        y: y + GameScene.ENTITY_LIGHT_OFFSET_Y,
        radius: GameScene.ENTITY_LIGHT_RADIUS,
        intensity: GameScene.ENTITY_LIGHT_INTENSITY,
        color: GameScene.ENTITY_LIGHT_COLOR,
        flicker: false
      });
    }
  }
  
  /**
   * Updates an entity's light position.
   */
  updateEntityLight(entityId: string, x: number, y: number): void {
    if (this.scene.lightingSystem) {
      this.scene.lightingSystem.updateLightPosition(`entity_${entityId}`, x, y + GameScene.ENTITY_LIGHT_OFFSET_Y);
    }
  }
  
  /**
   * Removes an entity's light source.
   */
  removeEntityLight(entityId: string): void {
    if (this.scene.lightingSystem) {
      this.scene.lightingSystem.removeLightSource(`entity_${entityId}`);
    }
  }

}
